import Tooltip from '@components/Tooltip/Tooltip';
import WithPlaceholder from '@components/WithPlaceholder/WithPlaceholder';
import { cardEditContext } from '@context/card-edit-context';
import { cardModeContext } from '@context/card-mode-context';
import { cardPreviewContext } from '@context/card-preview-context';
import { getOrCreateContext } from '@context/singleton-manager';
import { useCardAttribute } from '@hooks/use-card-attributes';
import { UpdateNameFieldAction, useCardNameState } from '@hooks/use-card-names-state';
import useNode from '@hooks/use-node';
import { constructFullName } from '@modules/card-helpers';
import { Box, Stack } from '@ui-lib';
import { type KeyboardEvent, useContext, useMemo, useRef, useState } from 'react';

import styles from '../Card/Card.module.scss';
import { FieldEditor } from '../CardField/components/FieldEditor';
import { CardItemEditor } from '../CardItemEditor';
import { EditIndicator } from '../EditIndicator';
import { LockIndicator } from '../LockIndicator';
import { LockToggle } from '../LockToggle';
import { CardFlavours } from '../types';
import { useCardNameMutation } from './hooks';
import { NameFields as NameInputFields } from './NameFields';
import type { NameFields } from './types';
import type { ValueProvider } from '.cache/__types__';

const ReadOnlyName = ({
  fullName,
  pronouns,
  isTemplate,
}: {
  fullName: string;
  pronouns: string;
  isTemplate?: boolean;
}) => (
  <div className={styles['user-name-container']}>
    <div className={styles['card-field']}>
      <div className={styles['card-field-btn']}>
        <WithPlaceholder placeholderText="Name" show={fullName === ''}>
          {(props) => (
            <>
              {isTemplate ? (
                <Tooltip
                  label="Name not editable on template"
                  aria-label="Name not editable on template"
                  size="small"
                >
                  <div className={styles['user-name']} {...props}>
                    {fullName}
                  </div>
                </Tooltip>
              ) : (
                <div className={styles['user-name']} {...props}>
                  {fullName}
                </div>
              )}
              {pronouns && <small className={styles['user-pronouns']}>({pronouns})</small>}
            </>
          )}
        </WithPlaceholder>
      </div>
    </div>
  </div>
);

const outlineContext = getOrCreateContext('card-outline');

interface NameProps {
  cardId: string;
  fields: NameFields;
}

export const Name = ({ cardId, fields }: NameProps): JSX.Element => {
  const noFields =
    !fields.first_name?.value &&
    !fields.last_name?.value &&
    !fields.honorific_prefix?.value &&
    !fields.honorific_suffix?.value &&
    !fields.maiden_name?.value &&
    !fields.middle_name?.value &&
    !fields.preferred_name?.value &&
    !fields.pronouns?.value;

  const cardAttributeType = useCardAttribute('first_name');
  const id = cardAttributeType?.id || '';

  const [showEditIcon, setShowEditIcon] = useState(false);

  const {
    activeAttributeId,
    flavour,
    isCardOwner,
    requestToMakeActive: requestToEdit,
    showRequiredAttributesErrors,
  } = useContext(cardEditContext);
  const { hideEmptyAttributes } = useContext(cardPreviewContext);
  const { mode } = useContext(cardModeContext);
  const [tempValueProviderForTour, setTempValueProviderForTour] = useState<ValueProvider | null>(null);

  const { activeId, requestToMakeActive, makeInactive, unlock: unlockOutline } = useContext(outlineContext);

  const [fieldNode, fieldRef] = useNode<HTMLDivElement | HTMLButtonElement>();
  const [fieldBtnNode, fieldBtnRef] = useNode<HTMLDivElement | HTMLButtonElement>();

  const { state, updateState, onCancel, fullyLocked, isStateModified } = useCardNameState(
    fields,
    Boolean(mode === 'edit'),
  );
  const cardNameMutation = useCardNameMutation({ cardId, fields, state });

  const fullName = constructFullName(state);

  const shouldMakeInactiveRef = useRef(false);

  const isActive = activeId == id;

  const isLocked = fields.first_name?.value_locked;

  const showHoverStateOnActiveAttribute = activeAttributeId === id;

  function handleMouseEnter() {
    if (mode !== 'edit') return;
    shouldMakeInactiveRef.current = false;
    requestToMakeActive(id);
    setShowEditIcon(true);
  }

  function handleFocus() {
    if (mode !== 'edit') return;
    shouldMakeInactiveRef.current = false;
    requestToMakeActive(id);
  }

  function handleBlur() {
    if (mode !== 'edit') return;
    makeInactive(id);
  }

  function handleMouseLeave() {
    if (mode !== 'edit') return;
    shouldMakeInactiveRef.current = true;
    setShowEditIcon(false);
    setTimeout(() => {
      if (shouldMakeInactiveRef.current) makeInactive(id);
    }, 300);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    // This must be keyDown otherwise submitting the form in the edit popover will reopen the edit popover
    if (event.key === 'Enter') {
      requestToEdit(id, 'update');
    }
  }

  function handleFieldClick() {
    unlockOutline({ command: 'activate-last-request' });
    requestToEdit(id, 'update');
  }

  const isFormValid = useMemo(
    // If either firstName or lastName is empty AND the fields are locked, the form is not valid, as we don't want to save empty fiels
    () => !((!state.firstName || !state.lastName) && state.locked),
    [state.firstName, state.lastName, state.locked],
  );

  const handleSaveNameFields = () => {
    // Prevent save action if fields are locked, but empty
    if (!isFormValid) return;

    cardNameMutation.update();
  };

  // if hideEmptyAttributes specified as true and there are no name fields, then name shouldn't be shown on the card at all
  if (hideEmptyAttributes && noFields) {
    return <></>;
  }

  if (mode !== 'edit') {
    return <ReadOnlyName fullName={fullName} pronouns={state.pronouns} />;
  }

  if (flavour === 'template') {
    return <ReadOnlyName fullName={fullName} pronouns={state.pronouns} isTemplate={true} />;
  }

  return (
    <>
      <div
        className={styles['user-name-container']}
        data-required-error={showRequiredAttributesErrors && noFields}
        data-editing-card-hover={true}
        data-active-attribute={showHoverStateOnActiveAttribute}
      >
        <div
          className={styles['card-field']}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          ref={fieldRef}
        >
          <button
            className={styles['card-field-btn']}
            data-id={id}
            data-is-locked={isLocked}
            onBlur={handleBlur}
            onClick={handleFieldClick}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            ref={fieldBtnRef}
          >
            {isLocked && isActive && <LockIndicator tooltipMessage={'Name field is locked'} />}

            <WithPlaceholder placeholderText="Name" show={fullName === ''}>
              {(props) => (
                <>
                  <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                    <Box>
                      <div className={styles['user-name']} {...props}>
                        {fullName}
                      </div>
                      {state.pronouns && (
                        <small className={styles['user-pronouns']}>({state.pronouns})</small>
                      )}
                    </Box>

                    {!isLocked && showEditIcon && <EditIndicator />}
                  </Box>
                </>
              )}
            </WithPlaceholder>
          </button>
        </div>
      </div>

      {!fullyLocked && (
        <FieldEditor
          title="Name"
          id={cardAttributeType?.id || ''}
          updateTempValueProviderForTour={setTempValueProviderForTour}
          fieldBtnNode={fieldBtnNode}
          fieldNode={fieldNode}
          hasUnsavedChanges={isStateModified}
          positionDeps={[]}
          onCancel={onCancel}
          onSave={handleSaveNameFields}
          validator={() => isFormValid}
          canDelete={false}
          isFieldLinkedToProfile={state.valueProvider === 'profile'}
          unlinkCardField={cardNameMutation.update}
        >
          <CardItemEditor
            valueProvider={state.valueProvider}
            tempValueProviderForTour={tempValueProviderForTour}
            setValueProvider={(value) => {
              if (state.valueProvider === 'admin') {
                // If an admin changes the field's value provider to member in order to make the member provide the value
                // we want to reset the fields ready for them.
                updateState({ type: UpdateNameFieldAction.RESET_NAME_FIELDS });
              }
              updateState({ type: UpdateNameFieldAction.UPDATE_VALUE_PROVIDER, value });
            }}
            stage={fields.first_name?.stage ?? undefined}
          >
            <Stack gap="large">
              <NameInputFields state={state} updateState={updateState} />

              {!isCardOwner && flavour === CardFlavours.TEAM && (
                <LockToggle
                  locked={state.locked}
                  onToggle={() => updateState({ type: UpdateNameFieldAction.TOGGLE_LOCK })}
                  lockedMessage="Card owners cannot change this field"
                  unlockedMessage="Allow card owners to change this field"
                />
              )}
            </Stack>
          </CardItemEditor>
        </FieldEditor>
      )}
    </>
  );
};
